import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  addOrientationChangeListener,
  removeOrientationChangeListener,
  getMaxResolutionWidth,
  getMaxResolutionHeight,
  getScreenResolutionWidth,
  getScreenResolutionHeight,
  setMaxResolution
} from "../../utils/screen-orientation-utils";
import { Input, Label, Slider, makeStyles } from "@fluentui/react-components";
import { useIntl } from "react-intl";

const useStyles = makeStyles({
  resolutionInput: {
    width: "80px",
    textAlign: "center"
  },
  maxResolutionSliderItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end"
  }
});

/**
 * Functional component to handle Max Resolution Preferences.
 *
 * This component displays the maximum resolution width and height,
 * and provides a slider to adjust the resolution multiplier.
 * It listens to orientation changes to update the displayed values accordingly.
 *
 * @param {object} props - Component properties.
 * @param {object} props.store - The store object containing preferences.
 * @returns {JSX.Element} The rendered component.
 */
const MaxResolutionSlider = ({ store }) => {
  /**
   * Callback to handle orientation changes.
   * Forces a re-render by updating the state.
   */
  const handleOrientationChange = useCallback(() => {
    // Since the component relies on store and orientation,
    // updating state to trigger a re-render.
    // Alternatively, you can use a state variable if needed.
    // Here, we use a state-less approach by leveraging the store updates.
    // If orientation affects the store, ensure the store emits an update.
    // Otherwise, you might need to introduce a state to force re-render.
  }, []);

  useEffect(() => {
    // Add orientation change listener on mount
    addOrientationChangeListener(handleOrientationChange);

    // Cleanup listener on unmount
    return () => {
      removeOrientationChangeListener(handleOrientationChange);
    };
  }, [handleOrientationChange]);

  /**
   * Handles changes from the slider to update the max resolution.
   *
   * @param {number} multiplier - The new multiplier value from the slider.
   */
  const onChange = multiplier => {
    const newWidth = Math.floor(getScreenResolutionWidth() * multiplier);
    const newHeight = Math.floor(getScreenResolutionHeight() * multiplier);
    setMaxResolution(store, newWidth, newHeight);
  };

  // Retrieve current max resolution values from the store
  const maxWidth = getMaxResolutionWidth(store);
  const maxHeight = getMaxResolutionHeight(store);

  // Calculate the current multiplier based on screen resolution
  const screenWidth = getScreenResolutionWidth();
  const multiplier = screenWidth > 0 ? maxWidth / screenWidth : 1;

  const intl = useIntl();
  const styles = useStyles();

  return (
    <>
      <Label htmlFor="max-resolution-slider">
        {intl.formatMessage({
          id: "preferences-screen.preference.max-resolution",
          defaultMessage: "Max Resolution"
        })}
      </Label>
      <div className={styles.maxResolutionSliderItem}>
        <Input
          className={styles.resolutionInput}
          tabIndex="0"
          type="number"
          step="1"
          min="0"
          value={maxWidth}
          readOnly
          appearance="filled-darker"
        />
        &nbsp;&times;&nbsp;
        <Input
          className={styles.resolutionInput}
          tabIndex="0"
          type="number"
          step="1"
          min="0"
          value={maxHeight}
          readOnly
          appearance="filled-darker"
        />
        <Slider
          step={0.1}
          min={0.1}
          max={1.0}
          value={multiplier}
          onChange={(_, data) => {
            onChange(data.value);
          }}
          id="max-resolution-slider"
        />
      </div>
    </>
  );
};

MaxResolutionSlider.propTypes = {
  /**
   * The store object containing the application's state and preferences.
   */
  store: PropTypes.object.isRequired
};

export default MaxResolutionSlider;
